import { Injectable } from '@angular/core';
import { HeaderFooterMachCore } from '@com-delta-idp/mnx-sitewide-shared-core-smart';
import {
  AirportDropDown,
  DestinationType,
  FROM,
  TO
} from '@com-delta-idp/mnx-sitewide-shared-core-ui';
import {
  CitiesDetails,
  SearchCitiesDetails
} from '@com-delta-idp/mnx-sitewide-shared-utils';
import { SearchItem } from '@com-delta-omnichannel/mach-core-js';
import {
  INVALID_DEST_ERROR_MSG,
  INVALID_DEST_PACKAGE_ERROR_MSG,
  INVALID_ORIGIN_ERROR_MSG
} from '../../constants/booking-vacation/booking-vacation.constants';

@Injectable({
  providedIn: 'root'
})
export class AirportTransformerService {
  transformToModalContent(
    data: SearchCitiesDetails,
    searchTerm: string,
    aem: HeaderFooterMachCore,
    destinationType: DestinationType,
    selectedCode = ''
  ): AirportDropDown {
    return {
      airportList: {
        id: 'vacation-search-transparent',
        title: this.getTitle(destinationType, aem),
        placeholder:
          aem.header?.book.vacations.airportPicker.labels.searchAirportLabel,
        hasExternalFiltering: true,
        shouldAutoCapitalize: true,
        menuVariant: 'transparent',
        hasAutocomplete: false,
        items: this.transformCitiesToAirportList(data.cities, selectedCode),
        errorConfig: {
          validateOnInput: true,
          message: this.getErrorMessageResults(data.errorCode, searchTerm)
        }
      },
      tabOptions: {
        id: 'tabs-width-fixed-small',
        tabVariant: 'standard',
        tabSize: 'small',
        tabWidth: 'fixed',
        tabItems: [
          {
            id: 'search',
            label: aem.header?.book?.vacations?.searchOptions?.placeHolderLabel,
            value: aem.header?.book?.vacations?.searchOptions?.placeHolderLabel,
            selected: true
          }
        ]
      },
      mobileCloseHeaderText: this.getCloseHeaderText(destinationType, aem),
      floatingLabelText:
        aem.header?.book?.vacations?.airportPicker?.labels?.floatingLabel
    };
  }

  getTitle(
    destinationType: DestinationType,
    aem: HeaderFooterMachCore
  ): string {
    return destinationType === FROM
      ? aem.header?.book.vacations.airportPicker.origin.placeHolderLabel
      : aem.header?.book.vacations.airportPicker.destination.placeHolderLabel;
  }

  getCloseHeaderText(
    destinationType: DestinationType,
    aem: HeaderFooterMachCore
  ): string {
    const { closeHeaderLabel } = aem.header.book.vacations.airportPicker.labels;
    const { origin, destination } = aem.header.book.vacations.airportPicker;

    const placeholder =
      destinationType === TO
        ? destination.placeHolderLabel
        : origin.placeHolderLabel;

    return destinationType === TO || destinationType === FROM
      ? closeHeaderLabel.replace('#', placeholder)
      : origin.placeHolderLabel;
  }

  transformCitiesToAirportList(
    cities: CitiesDetails[],
    selectedCode = ''
  ): SearchItem[] {
    return cities.map((city, index) => ({
      id: index.toString(),
      code: city.airportCode || '',
      label: this.formatAirportName(city),
      value: city.airportCode || '',
      selected: !!selectedCode && city.airportCode === selectedCode
    }));
  }

  private formatAirportName(city: CitiesDetails): string {
    const location = this.getLocationName(city);
    return `${city.airportCode || ''} ${city.cityName || ''}, ${location} (${
      city.airportCode || ''
    })`;
  }

  private getLocationName(city: CitiesDetails): string {
    return city.stateName || city.countryName || city.airportName || '';
  }

  private getErrorMessageResults(
    errorCode: string | undefined,
    searchTerm: string
  ): string {
    const errorMessages = {
      invalidDestErrMsg: INVALID_DEST_ERROR_MSG,
      invalidDestPkgErrMsg: INVALID_DEST_PACKAGE_ERROR_MSG,
      invalidOriginErrMsg: INVALID_ORIGIN_ERROR_MSG
    };

    const message = errorCode
      ? errorMessages[errorCode as keyof typeof errorMessages] || ''
      : '';

    return this.replaceHashTag(message, searchTerm);
  }

  replaceHashTag(message: string, searchTerm: string): string {
    return message.replace('#', searchTerm);
  }
}
